import { useState, useMemo, useRef, forwardRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { FileText, Shield, Handshake, Briefcase, Download, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";

const A4_W = 794;
const A4_H = 1123;

/* ============================== TEMPLATES ==============================
   Each template exposes:
   - id, title, description, icon, gradient
   - fields: [{key, label, type, placeholder, hint, defaultFrom(user)}]
   - render(values, user) -> ReactNode (styled contract preview at A4 dimensions)
*/

const inr = (n) => Number(n || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "________________";

const TEMPLATES = [
  {
    id: "nda",
    title: "Mutual NDA",
    description: "Standard mutual non-disclosure agreement — protect confidential business discussions with a partner, vendor, or advisor.",
    icon: Shield,
    tone: "aubergine",
    fields: [
      { key: "party1_name",    label: "Your legal name / entity",    type: "text",     required: true, defaultFrom: (u) => u.business || u.name || "" },
      { key: "party1_address", label: "Your address",                type: "textarea", placeholder: "Registered address" },
      { key: "party2_name",    label: "Other party name / entity",   type: "text",     required: true },
      { key: "party2_address", label: "Other party address",         type: "textarea" },
      { key: "purpose",        label: "Purpose of discussion",       type: "textarea", required: true, placeholder: "e.g. Exploring a co-branded product launch between our brands" },
      { key: "term_months",    label: "Confidentiality term (months)", type: "number", required: true, placeholder: "24" },
      { key: "jurisdiction",   label: "Jurisdiction (city)",         type: "text",     required: true, defaultFrom: (u) => u.city || "Hyderabad" },
      { key: "date",           label: "Effective date",              type: "date",     required: true, defaultFrom: () => today() },
    ],
    render: (v) => (
      <ContractA4 title="Mutual Non-Disclosure Agreement">
        <p style={pageP}>
          This Mutual Non-Disclosure Agreement (this <b>&ldquo;Agreement&rdquo;</b>) is entered into as of <b>{fmtDate(v.date)}</b> by and between:
        </p>
        <PartyBlock label="Party A" name={v.party1_name} address={v.party1_address} />
        <PartyBlock label="Party B" name={v.party2_name} address={v.party2_address} />
        <Clause n={1} h="Purpose">
          The Parties intend to explore a potential business relationship for the following purpose (the <b>&ldquo;Purpose&rdquo;</b>):
          {" "}<i>{v.purpose || "________________"}</i>.
        </Clause>
        <Clause n={2} h="Confidential Information">
          &ldquo;Confidential Information&rdquo; means all non-public information disclosed by one party (Discloser) to the other (Recipient), whether oral, written, electronic or visual, including but not limited to business plans, financials, customer data, product designs, trade secrets, and know-how.
        </Clause>
        <Clause n={3} h="Obligations">
          Recipient shall (a) hold Confidential Information in strict confidence, (b) use it solely for the Purpose, (c) protect it with the same degree of care it uses for its own confidential information (and no less than reasonable care), and (d) not disclose it to any third party without Discloser&rsquo;s prior written consent.
        </Clause>
        <Clause n={4} h="Term">
          The obligations set forth herein shall remain in effect for <b>{v.term_months || "___"} months</b> from the Effective Date.
        </Clause>
        <Clause n={5} h="Return of Materials">
          Upon written request, Recipient shall promptly return or destroy all Confidential Information and certify such destruction in writing.
        </Clause>
        <Clause n={6} h="No License">
          Nothing in this Agreement grants either party any license or right in the other party&rsquo;s intellectual property.
        </Clause>
        <Clause n={7} h="Governing Law &amp; Jurisdiction">
          This Agreement shall be governed by the laws of India. The courts at <b>{v.jurisdiction || "________"}</b> shall have exclusive jurisdiction.
        </Clause>
        <SignBlock a={v.party1_name} b={v.party2_name} />
      </ContractA4>
    ),
  },
  {
    id: "service",
    title: "Service Agreement",
    description: "For consultants, agencies, freelancers — defines scope, deliverables, fees, timelines, IP ownership.",
    icon: Handshake,
    tone: "rose",
    fields: [
      { key: "provider_name",   label: "Service provider (you)",     type: "text",     required: true, defaultFrom: (u) => u.business || u.name || "" },
      { key: "provider_email",  label: "Provider email",             type: "text",     defaultFrom: (u) => u.email || "" },
      { key: "client_name",     label: "Client name / company",      type: "text",     required: true },
      { key: "client_email",    label: "Client email",               type: "text" },
      { key: "services",        label: "Services / scope of work",   type: "textarea", required: true, placeholder: "e.g. Brand identity design — logo, colour system, typography, brand guidelines, social media templates" },
      { key: "deliverables",    label: "Key deliverables",           type: "textarea", placeholder: "One per line" },
      { key: "start_date",      label: "Start date",                 type: "date",     required: true, defaultFrom: () => today() },
      { key: "end_date",        label: "Completion date",            type: "date" },
      { key: "fee_amount",      label: "Total fee (₹)",              type: "number",   required: true, placeholder: "150000" },
      { key: "payment_terms",   label: "Payment terms",              type: "text",     defaultFrom: () => "50% advance, 50% on final delivery", placeholder: "50% advance, 50% on delivery" },
      { key: "jurisdiction",    label: "Jurisdiction (city)",        type: "text",     required: true, defaultFrom: (u) => u.city || "Hyderabad" },
    ],
    render: (v) => (
      <ContractA4 title="Service Agreement">
        <p style={pageP}>
          This Service Agreement (this <b>&ldquo;Agreement&rdquo;</b>) is entered into as of <b>{fmtDate(v.start_date)}</b> by and between:
        </p>
        <PartyBlock label="Service Provider" name={v.provider_name} address={v.provider_email} />
        <PartyBlock label="Client" name={v.client_name} address={v.client_email} />
        <Clause n={1} h="Services">
          Provider shall provide the following services to Client (the <b>&ldquo;Services&rdquo;</b>):
          <div style={{ ...quoteBlock }}>{v.services || "________________"}</div>
        </Clause>
        <Clause n={2} h="Deliverables">
          {v.deliverables ? (
            <ul style={{ margin: "6px 0 0 20px", padding: 0 }}>
              {v.deliverables.split(/\n+/).filter(Boolean).map((d, i) => <li key={i} style={{ margin: "4px 0" }}>{d.trim()}</li>)}
            </ul>
          ) : "As mutually agreed in writing between the Parties."}
        </Clause>
        <Clause n={3} h="Timeline">
          Services shall commence on <b>{fmtDate(v.start_date)}</b>{v.end_date ? <> and be completed by <b>{fmtDate(v.end_date)}</b></> : null}, subject to timely inputs from the Client.
        </Clause>
        <Clause n={4} h="Fees &amp; Payment">
          Total consideration for the Services is <b>₹{inr(v.fee_amount)}</b> (Indian Rupees {inr(v.fee_amount)} only), exclusive of applicable taxes. Payment terms: <i>{v.payment_terms || "as agreed"}</i>. Invoices are payable within <b>7 days</b> of receipt.
        </Clause>
        <Clause n={5} h="Intellectual Property">
          Upon full payment, all final deliverables shall vest with the Client. Provider retains ownership of all pre-existing IP, tools, methodologies, and the right to display finished work in a portfolio.
        </Clause>
        <Clause n={6} h="Confidentiality">
          Each party shall keep the other&rsquo;s confidential information private and use it solely to perform this Agreement.
        </Clause>
        <Clause n={7} h="Termination">
          Either party may terminate this Agreement with 15 days&rsquo; written notice. Client shall pay for all Services rendered up to the effective date of termination.
        </Clause>
        <Clause n={8} h="Governing Law">
          This Agreement is governed by the laws of India; courts at <b>{v.jurisdiction || "________"}</b> shall have exclusive jurisdiction.
        </Clause>
        <SignBlock a={v.provider_name} b={v.client_name} />
      </ContractA4>
    ),
  },
  {
    id: "freelance",
    title: "Freelance Contract",
    description: "Lightweight project engagement — great for one-off gigs, retainers, and short creative sprints.",
    icon: Briefcase,
    tone: "champagne",
    fields: [
      { key: "freelancer_name",  label: "Freelancer (you)",           type: "text",     required: true, defaultFrom: (u) => u.name || "" },
      { key: "freelancer_email", label: "Freelancer email",           type: "text",     defaultFrom: (u) => u.email || "" },
      { key: "client_name",      label: "Client name",                type: "text",     required: true },
      { key: "project_title",    label: "Project title",              type: "text",     required: true, placeholder: "e.g. Website redesign" },
      { key: "scope",            label: "Scope of work",              type: "textarea", required: true, placeholder: "Describe what you'll deliver" },
      { key: "start_date",       label: "Start date",                 type: "date",     defaultFrom: () => today() },
      { key: "duration_weeks",   label: "Duration (weeks)",           type: "number",   placeholder: "4" },
      { key: "fee_amount",       label: "Project fee (₹)",            type: "number",   required: true },
      { key: "revisions",        label: "Included revision rounds",   type: "number",   defaultFrom: () => 2 },
      { key: "jurisdiction",     label: "Jurisdiction (city)",        type: "text",     defaultFrom: (u) => u.city || "Hyderabad" },
    ],
    render: (v) => (
      <ContractA4 title="Freelance Project Agreement">
        <p style={pageP}>
          This Freelance Project Agreement is entered into on <b>{fmtDate(v.start_date)}</b> between <b>{v.freelancer_name || "________"}</b> (&ldquo;Freelancer&rdquo;) and <b>{v.client_name || "________"}</b> (&ldquo;Client&rdquo;) for the project titled <i>&ldquo;{v.project_title || "________"}&rdquo;</i>.
        </p>
        <Clause n={1} h="Scope of Work">
          <div style={{ ...quoteBlock }}>{v.scope || "________________"}</div>
        </Clause>
        <Clause n={2} h="Timeline">
          Project shall commence on <b>{fmtDate(v.start_date)}</b>{v.duration_weeks ? <> and shall be delivered within <b>{v.duration_weeks} weeks</b></> : null}, subject to timely feedback and inputs from the Client.
        </Clause>
        <Clause n={3} h="Fees">
          Total project fee: <b>₹{inr(v.fee_amount)}</b>, payable 50% upfront and 50% on final delivery. All taxes extra as applicable.
        </Clause>
        <Clause n={4} h="Revisions">
          The project fee includes up to <b>{v.revisions || "2"}</b> rounds of revisions. Additional revisions will be billed hourly at ₹2,500/hr.
        </Clause>
        <Clause n={5} h="Ownership">
          Upon full payment, Client receives ownership of the final deliverables. Freelancer retains portfolio &amp; showcase rights.
        </Clause>
        <Clause n={6} h="Communication">
          Primary channel: email (<i>{v.freelancer_email || "________"}</i>). Meetings by mutual convenience; response within 2 business days.
        </Clause>
        <Clause n={7} h="Cancellation">
          Either party may cancel with 7 days&rsquo; notice. Client shall pay for all work completed. The 50% advance is non-refundable once work has commenced.
        </Clause>
        <Clause n={8} h="Governing Law">
          This Agreement is governed by the laws of India; courts at <b>{v.jurisdiction || "________"}</b> shall have exclusive jurisdiction.
        </Clause>
        <SignBlock a={v.freelancer_name} b={v.client_name} />
      </ContractA4>
    ),
  },
];

const pageP = { margin: "0 0 14px", fontSize: 13, lineHeight: 1.6 };
const quoteBlock = { marginTop: 8, padding: "10px 14px", borderLeft: "3px solid #D9A55C", background: "rgba(217,165,92,0.06)", fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap" };

function ContractA4({ title, children }) {
  return (
    <div style={{
      width: A4_W, height: A4_H,
      background: "#FDFBF7",
      color: "#3A0F4D",
      fontFamily: '"Outfit", sans-serif',
      padding: "56px 68px",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 18, borderBottom: "2px solid #D9A55C" }}>
        <img src="/wowshe-logo.png" alt="WOWSHE" crossOrigin="anonymous" style={{ height: 42, display: "block" }} />
        <p style={{ margin: 0, fontSize: 9, letterSpacing: "0.32em", textTransform: "uppercase", color: "#9D2B6B" }}>Legal Document</p>
      </div>
      <h1 style={{ margin: "26px 0 20px", fontFamily: '"Playfair Display", serif', fontSize: 34, fontWeight: 500, letterSpacing: "-0.01em" }}>{title}</h1>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "#3A0F4D" }}>{children}</div>
      <p style={{ position: "absolute", left: 68, right: 68, bottom: 24, textAlign: "center", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B4769" }}>
        Generated via WOWSHE · wowshe.in
      </p>
    </div>
  );
}

function PartyBlock({ label, name, address }) {
  return (
    <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "rgba(58,15,77,0.04)", borderRadius: 6 }}>
      <p style={{ margin: 0, fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: "#9D2B6B" }}>{label}</p>
      <p style={{ margin: "3px 0 0", fontSize: 14, fontWeight: 600 }}>{name || "________________"}</p>
      {address && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#6B4769", whiteSpace: "pre-wrap" }}>{address}</p>}
    </div>
  );
}

function Clause({ n, h, children }) {
  return (
    <div style={{ margin: "12px 0" }}>
      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#9D2B6B" }}>{n}. {h}</p>
      <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function SignBlock({ a, b }) {
  return (
    <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
      {[a, b].map((name, i) => (
        <div key={i}>
          <div style={{ borderTop: "1px solid #3A0F4D", paddingTop: 8 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{name || "________________"}</p>
            <p style={{ margin: "2px 0 0", fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: "#6B4769" }}>Signature &amp; Date</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================== PAGE ============================== */

export default function Contracts() {
  const { user, loading } = useAuth();
  const [selectedId, setSelectedId] = useState(null);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const template = TEMPLATES.find(t => t.id === selectedId);

  return (
    <DashboardShell
      role={user.role === "admin" ? "admin" : "member"}
      title="Contract Templates"
      subtitle={template ? `Fill in the blanks — the rest auto-drafts for you` : "Ready-to-send legal templates for your business"}
    >
      <div data-testid="contracts-page">
        {!template ? (
          <TemplateGrid onSelect={setSelectedId} />
        ) : (
          <TemplateBuilder template={template} user={user} onBack={() => setSelectedId(null)} />
        )}
      </div>
    </DashboardShell>
  );
}

const TONE_CLASSES = {
  aubergine: "from-aubergine to-plum text-ivory",
  rose:      "from-rose to-roseLight text-ivory",
  champagne: "from-champagne to-champagneLight text-aubergine",
};

function TemplateGrid({ onSelect }) {
  return (
    <>
      <div className="mb-6 flex items-start gap-3 rounded-xl bg-aubergine/[0.04] border border-aubergine/[0.08] p-4">
        <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-aubergine shrink-0"><Sparkles className="w-4 h-4 text-champagne" /></div>
        <div>
          <p className="text-[13px] text-aubergine font-medium">Skip the boilerplate</p>
          <p className="text-xs text-plumSoft/80 mt-0.5">Pick a template, fill in the blanks — your business details auto-populate. Download an A4 PDF ready to sign.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TEMPLATES.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`contract-card-${t.id}`}
              onClick={() => onSelect(t.id)}
              className="text-left bg-white rounded-2xl border border-aubergine/[0.08] p-6 hover:border-aubergine/25 hover:shadow-luxe transition-all group"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${TONE_CLASSES[t.tone]} flex items-center justify-center mb-4`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl text-aubergine leading-tight">{t.title}</h3>
              <p className="text-[13px] text-plumSoft/85 mt-2 leading-relaxed">{t.description}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-champagne mt-4 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Use template <span>→</span>
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
}

function TemplateBuilder({ template, user, onBack }) {
  const initialValues = useMemo(() => {
    const v = {};
    template.fields.forEach(f => {
      v[f.key] = typeof f.defaultFrom === "function" ? (f.defaultFrom(user) ?? "") : "";
    });
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);
  const [values, setValues] = useState(initialValues);
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const download = async () => {
    setBusy(true);
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise(r => setTimeout(r, 250));
      const dataUrl = await toPng(canvasRef.current, {
        cacheBust: true, pixelRatio: 2, width: A4_W, height: A4_H, backgroundColor: "#FDFBF7",
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      pdf.addImage(dataUrl, "PNG", 0, 0, 210, 297);
      pdf.save(`${template.id}-${(values[template.fields[0].key] || "wowshe").toString().toLowerCase().replace(/\s+/g, "-").slice(0, 40)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not export");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="contract-back" onClick={onBack} className="inline-flex items-center gap-2 text-[13px] text-plum hover:text-aubergine mb-5">
        <ArrowLeft className="w-4 h-4" /> All templates
      </button>

      <div className="grid lg:grid-cols-[420px_1fr] gap-8">
        {/* Form */}
        <div className="bg-white rounded-2xl border border-aubergine/[0.08] p-6 h-fit">
          <h3 className="text-[13px] uppercase tracking-[0.14em] text-plumSoft/70 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-champagne" /> {template.title}
          </h3>

          <div className="space-y-4">
            {template.fields.map(f => (
              <div key={f.key}>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">
                  {f.label}{f.required && <span className="text-rose ml-0.5">*</span>}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    data-testid={`ct-${f.key}`}
                    rows={3}
                    value={values[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2 rounded-lg bg-[#FAF8F5] border border-aubergine/[0.08] text-[13px] text-aubergine focus:border-aubergine/40 focus:outline-none"
                  />
                ) : (
                  <input
                    data-testid={`ct-${f.key}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={values[f.key] ?? ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2 rounded-lg bg-[#FAF8F5] border border-aubergine/[0.08] text-[13px] text-aubergine focus:border-aubergine/40 focus:outline-none"
                  />
                )}
                {f.hint && <p className="text-[10px] text-plumSoft/60 mt-1">{f.hint}</p>}
              </div>
            ))}
          </div>

          <button
            data-testid="ct-download"
            onClick={download}
            disabled={busy}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-aubergine text-ivory px-5 py-3 rounded-full text-[12px] tracking-[0.14em] uppercase hover:bg-aubergine/90 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {busy ? "Preparing…" : "Download PDF (A4)"}
          </button>
          <p className="text-[10px] text-plumSoft/60 text-center mt-2">
            Templates are generic guidance only — for high-value deals, consult a lawyer.
          </p>
        </div>

        {/* Live preview */}
        <ContractPreview>
          <ContractCanvas ref={canvasRef}>
            {template.render(values)}
          </ContractCanvas>
        </ContractPreview>
      </div>
    </>
  );
}

function ContractPreview({ children }) {
  return (
    <div className="bg-[#F3EFE9] rounded-2xl border border-aubergine/[0.08] p-6 overflow-hidden">
      <div style={{ width: A4_W * 0.7, height: A4_H * 0.7, position: "relative", margin: "0 auto" }}>
        <div style={{ transform: "scale(0.7)", transformOrigin: "top left", width: A4_W, height: A4_H }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const ContractCanvas = forwardRef(function ContractCanvasInner({ children }, ref) {
  return <div ref={ref}>{children}</div>;
});
